import type { IconData } from "@loot-os/icons/types";
import { Button, type ButtonProps } from "../../core/Button.js";
import { buttonStyles } from "../../core/Button.styles.js";
import { Icon } from "../../core/Icon.js";
import { IconButton } from "../../core/IconButton.js";
import { iconButtonStyles } from "../../core/IconButton.styles.js";
import { useIconsStill } from "../../core/iconStill.js";
import { AppLink } from "../../foundations/AppLink.js";
import type { ActionContent } from "./content.js";

export interface ActionButtonProps {
  action: ActionContent;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  fullWidth?: boolean;
  /**
   * Draws the action as this icon alone (a phone bar's deposit), named by its label. Takes
   * IconButton's look, so `variant` and `fullWidth` don't apply.
   */
  iconOnly?: IconData;
  /**
   * The view's call to action (Button's `shine`): a light sweeps across it as the pointer arrives,
   * and its `icon` plays as the theme allows (a commit's, in a dialog's footer, stays still). Not
   * on `iconOnly`.
   */
  shine?: boolean;
  /** An icon before the label (Button's `icon`); not with `iconOnly`, which is the icon alone. */
  icon?: IconData;
  className?: string;
}

/**
 * A content action drawn as a button: a link in button styles when it goes somewhere (`href` or
 * `render`), so it routes and opens in a new tab like any link; a Button when it only acts.
 */
export function ActionButton({
  action,
  variant,
  size,
  fullWidth,
  iconOnly,
  shine = false,
  icon,
  className,
}: ActionButtonProps) {
  // Its icon plays on hover, as the theme's icons allow; a commit's (in a dialog's footer) stays
  // still, as Button's does.
  const commit = useIconsStill();
  const iconTrigger = commit ? undefined : "hover";
  const links = action.href !== undefined || action.render !== undefined;
  if (iconOnly) {
    const iconSize = size === "sm" ? "sm" : "md";
    if (!links) {
      return (
        <IconButton
          icon={iconOnly}
          label={action.label}
          size={iconSize}
          onClick={action.onClick}
          className={className}
        />
      );
    }
    return (
      <AppLink
        href={action.href}
        render={action.render}
        onClick={action.onClick}
        aria-label={action.label}
        className={iconButtonStyles({ size: iconSize, className })}
      >
        {/* An icon-only control: its icon plays on hover, as IconButton's does. */}
        <Icon icon={iconOnly} size={iconSize === "sm" ? 16 : 20} trigger="hover" />
      </AppLink>
    );
  }
  if (links) {
    return (
      <AppLink
        href={action.href}
        render={action.render}
        onClick={action.onClick}
        className={buttonStyles({ variant, size, fullWidth, className })}
      >
        {icon && <Icon icon={icon} trigger={iconTrigger} className="shrink-0" />}
        {action.label}
        {shine && <span aria-hidden className="shine-sweep" />}
      </AppLink>
    );
  }
  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      shine={shine}
      icon={icon}
      className={className}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );
}
